import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { authApi } from '../../../assets/api/services/authApi';
import { PROFILE_LANGUAGES } from '../../../assets/constants/profileData';
import { CurrentUserContext } from '../../../assets/contexts/currentUserContext';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import { RightArrowIcon } from '../../../assets/icons/icons';
import { triggerImpactHaptic } from '../../../assets/utils/hapticFeedback';
import ScreenHeader from '../../ScreenHeader/ScreenHeader';
import BottomSheet from '../../shared/BottomSheet/BottomSheet';
import '../profileSubpage.css';
import './ProfileLanguage.css';

function getCurrentLanguageId(currentUser, fallbackLanguage = 'en') {
  return currentUser?.personal_data?.language || fallbackLanguage;
}

function getLanguageLabel(languageId) {
  return PROFILE_LANGUAGES.find((language) => language.id === languageId)?.label
    || PROFILE_LANGUAGES[0]?.label
    || languageId;
}

export function ProfileLanguageSheet({ onClose }) {
  const { currentUser, setCurrentUser } = useContext(CurrentUserContext);
  const { language, setLanguage, profile } = useContext(TranslationContext);
  const [isSaving, setIsSaving] = useState(false);
  const selectedId = getCurrentLanguageId(currentUser, language);

  const handleSelect = async (languageId) => {
    if (isSaving || languageId === selectedId) {
      return;
    }

    triggerImpactHaptic('light');

    const previousLanguage = language;
    setIsSaving(true);
    setLanguage(languageId);

    try {
      await authApi.editPersonalData({
        setData: {
          language: languageId,
        },
      });

      setCurrentUser((prevUser) => {
        if (!prevUser) {
          return prevUser;
        }

        return {
          ...prevUser,
          personal_data: {
            ...(prevUser.personal_data || {}),
            language: languageId,
          },
        };
      });

      onClose();
    } catch {
      setLanguage(previousLanguage);
      toast.error(profile.languageUpdateFailed);
      setIsSaving(false);
    }
  };

  return (
    <BottomSheet
      className="profile-language-sheet"
      panelClassName="profile-language-sheet__panel"
      title={profile.language}
      titleId="profile-language-sheet-title"
      onClose={onClose}
    >
      <div className="profile-language-sheet__list">
        {PROFILE_LANGUAGES.map((language, index) => (
          <div key={language.id}>
            <button
              type="button"
              className="profile-language__option"
              disabled={isSaving}
              onClick={() => handleSelect(language.id)}
            >
              <span
                className={
                  selectedId === language.id
                    ? 'profile-language__label profile-language__label--active'
                    : 'profile-language__label'
                }
              >
                {language.label}
              </span>
              <span
                className={
                  selectedId === language.id
                    ? 'profile-subpage__radio profile-subpage__radio--checked'
                    : 'profile-subpage__radio'
                }
                aria-hidden="true"
              >
                {selectedId === language.id ? '✓' : null}
              </span>
            </button>
            {index < PROFILE_LANGUAGES.length - 1 && (
              <div className="profile-subpage__divider" />
            )}
          </div>
        ))}
      </div>
    </BottomSheet>
  );
}

function ProfileLanguage() {
  const navigate = useNavigate();
  const { currentUser } = useContext(CurrentUserContext);
  const { language, profile } = useContext(TranslationContext);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const currentLanguage = getCurrentLanguageId(currentUser, language);
  const openSheet = () => {
    triggerImpactHaptic('light');
    setIsSheetOpen(true);
  };

  return (
    <section className="profile-subpage profile-language">
      <ScreenHeader title={profile.language} onBack={() => navigate('/profile')} />

      <div className="profile-subpage__content">
        <h2 className="profile-subpage__section-label profile-subpage__section-label--first">
          {profile.preferencesSection}
        </h2>
        <button
          type="button"
          className="profile-subpage__card profile-language__current"
          onClick={openSheet}
        >
          <span className="profile-subpage__row-content">
            <span className="profile-subpage__row-label">{profile.currentLanguage}</span>
            <span className="profile-subpage__row-value">
              {getLanguageLabel(currentLanguage)}
            </span>
          </span>
          <RightArrowIcon className="profile-subpage__chevron" />
        </button>
      </div>

      <AnimatePresence>
        {isSheetOpen && (
          <ProfileLanguageSheet onClose={() => setIsSheetOpen(false)} />
        )}
      </AnimatePresence>
    </section>
  );
}

export default ProfileLanguage;
